import { FieldPath, FieldValues } from "react-hook-form";
import Select, { GroupBase, MultiValue, OptionsOrGroups, PropsValue, SingleValue } from "react-select";

import { HubInputProps } from "./types";
import { useRef } from "react";

const HubInputField =
  <T extends FieldValues, K extends FieldPath<T>>({
    isRowBased,
    isRowInsideAnother,
    inputLabel,
    inputId,
    isRequired,
    inputPlaceholder,
    inputType,
    field,
    error,
    onKeyDown,
    onBlur,
    isDropdown,
    isSearchable,
    dropdownData,
    isMulti,
    getSearchPhraseFromDropdown,inputRef,isTypeAhead,

  }: HubInputProps<T, K>): JSX.Element =>
{

    const searchRef = useRef("");
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getValue = (value?: string | string[]) => {
    if (dropdownData) {
      if (!value || value === "") return null;

      const data = dropdownData.find((option) => {
        if (Array.isArray(value)) {
          return value.indexOf((option as unknown as { value: string; label: string; }).value) > -1;
        } else {
          return (option as unknown as { value: string; label: string; }).value === value;
        }
      });

      if (data) return data as unknown as PropsValue<string>;
    }
  };

  const onChangeSelect = (value: SingleValue<string | { label: string; value: string; }> | MultiValue<string | { label: string; value: string; }>) => {
    if (Array.isArray(value))
      field.onChange(
        value.map((val) => (val as unknown as { value: string; label: string; }).value)
      );
    else
      field.onChange((value as unknown as { value: string; label: string; }).value);
  };

  const onBlurInput = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (onBlur) onBlur(event)
    field.onBlur();
  };

   const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isTypeAhead || !dropdownData || dropdownData.length === 0) return;

        // Ignore special keys
        if (e.key.length !== 1 && e.key !== "Backspace") return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (e.key === "Backspace") {
            searchRef.current = searchRef.current.slice(0, -1);
        } else {
            searchRef.current += e.key.toLowerCase();
        }

        const flatOptions: { label: string; value: string }[] = (
            dropdownData as unknown as OptionsOrGroups<
                { label: string; value: string },
                GroupBase<{ label: string; value: string }>
            >
        ).flatMap(opt =>
            "options" in opt ? opt.options : [opt]
        );

        const match = flatOptions.find(opt =>
            opt.label.toLowerCase().startsWith(searchRef.current)
        );

        if (match) {
            field.onChange(match.value);
        }

        timeoutRef.current = setTimeout(() => {
            searchRef.current = "";
        }, 200);
    };

  const RowBasedInput = (
    <div className="row mb-2">
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label col-lg-2 col-md-4 col-12">{inputLabel} {isRequired ? '*' : null}</label>
      ) : null}
      <div className={inputLabel ? "col-lg-10 col-md-8 col-12" : "col-12"}>
        {isDropdown ? (
          <Select
            options={dropdownData}
            id={inputId}
            placeholder={inputPlaceholder}
            classNames={{
              control(_props) {
                return `input-group ${error && error.message ? "border-danger" : ""}`
              },
              menu(_props) {
                return "custom-dropdown-menu"
              },
            }}
            isSearchable={isSearchable}
            isMulti={isMulti}
            {...field}
            onBlur={onBlurInput}
            noOptionsMessage={() => "Select Option..."}
            value={getValue(field.value)}
            onChange={(value) => onChangeSelect(value)}
            onInputChange={getSearchPhraseFromDropdown}
            onKeyDown={handleKeyDown}

          />
        ) : (
          <input type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}`} id={inputId} placeholder={inputPlaceholder} onKeyDown={onKeyDown} {...field} onBlur={onBlurInput}   ref={inputRef || field.ref}
/>
        )}
        {error && error.message ? (
          <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
        ) : null}
      </div>
    </div>
  );

  const RowInsideRowBasedInput = (
    <div className="row mb-2">
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label col-lg-4 col-md-4 col-12">
            {inputLabel} {isRequired ? '*' : null}
        </label>
      ) : null}
      <div className={inputLabel ? 'col-lg-8 col-md-8 col-12' : 'col-12'}>
        {isDropdown ? (
          <Select
            options={dropdownData}
            id={inputId}
            classNames={{
              control(_props) {
                return `input-group ${error && error.message ? "border-danger" : ""}`
              },
              menu(_props) {
                return "custom-dropdown-menu"
              },
            }}
            placeholder={inputPlaceholder}
            isSearchable={isSearchable}
            isMulti={isMulti}
            {...field}
            onBlur={onBlurInput}
            noOptionsMessage={() => "Select Option..."}
            value={getValue(field.value)}
            onChange={(value) => onChangeSelect(value)}
            onInputChange={getSearchPhraseFromDropdown}
            onKeyDown={handleKeyDown}

          />
        ) : (
          <input type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}`} id={inputId} placeholder={inputPlaceholder} onKeyDown={onKeyDown} {...field} onBlur={onBlurInput}   ref={inputRef || field.ref}
/>
        )}
        {error && error.message ? (
          <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
        ) : null}
      </div>
    </div>
  );

  const SimpleInput = (
    <div className="mb-3">
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label justify-content-start">{inputLabel} {isRequired ? '*' : null}</label>
      ) : null}
      {isDropdown ? (
        <Select
          options={dropdownData}
          id={inputId}
          placeholder={inputPlaceholder}
          classNames={{
              control(_props) {
                  return `input-group ${error && error.message ? "border-danger" : ""}`
              },
              menu(_props) {
                return "custom-dropdown-menu"
              }
          }}
          isSearchable={isSearchable}
          isMulti={isMulti}
          {...field}
          onBlur={onBlurInput}
          noOptionsMessage={() => "Select Option..."}
          value={getValue(field.value)}
          onChange={(value) => onChangeSelect(value)}
          onInputChange={getSearchPhraseFromDropdown}
          onKeyDown={handleKeyDown}

        />
      ) : (
        <input type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}`} placeholder={inputPlaceholder} onKeyDown={onKeyDown} id={inputId} {...field} onBlur={onBlurInput}   ref={inputRef || field.ref}
/>
      )}
      {error && error.message ? (
          <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
      ) : null}
    </div>
  );

  return isRowBased ? (isRowInsideAnother ? RowInsideRowBasedInput : RowBasedInput) : SimpleInput;
};

export default HubInputField;
